import {useCallback, useEffect, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {Formik} from 'formik';
import * as yup from 'yup';
import DropDownPicker, {
  ItemType,
  ValueType,
} from 'react-native-dropdown-picker';
import {
  Button,
  Checkbox,
  Headline,
  HelperText,
  IconButton,
  Text,
  TextInput,
} from 'react-native-paper';
import {DatePickerModal} from 'react-native-paper-dates';
import {FirebaseFirestoreTypes} from '@react-native-firebase/firestore';

import {useLayoutInfo} from '../hooks/useLayoutInfo';
import {useAppSettings} from './AppSettings';

export enum FormikStoreTypes {
  'text',
  'textarea',
  'date',
  'number',
  'select',
  'boolean',
  'arraySelect', // TODO document that you need to send a *copy* in here, arrays are passed by reference
}

export type FormikStoreType =
  | string
  | number
  | Date
  | FirebaseFirestoreTypes.Timestamp
  | boolean
  | FirebaseFirestoreTypes.DocumentReference
  | Array<
      | string
      | number
      | Date
      | FirebaseFirestoreTypes.Timestamp
      | boolean
      | FirebaseFirestoreTypes.DocumentReference
    >
  | undefined;

export type FormikStoreValidator = (
  value: FormikStoreType,
  required: boolean,
  formikStoreObjects: FormikStoreObject[],
) => boolean;

export const DefaultFormikStoreValidator: FormikStoreValidator = (
  value,
  required,
) => {
  if (value === undefined && required) {
    return false;
  }

  if (typeof value === 'string' && required && value.length < 1) {
    return false;
  }

  if (required && Array.isArray(value) && value.length < 1) {
    return false;
  }

  return true;
};

export type FormikStoreSelectItemsMetadata = {
  items: FormikStoreType[];
  // if the items are DocumentReference objects, these are the cooresponding documents for label extraction
  // Note that the DocumentReference objects in items should be in same order as DocumentData in documents,
  // they are referenced by corresponding index value to map back and forth between the two
  documents?: readonly FirebaseFirestoreTypes.DocumentData[];
  labelExtractor?: (
    item: FirebaseFirestoreTypes.DocumentData | undefined,
  ) => string;
};

export type FormikStoreFieldMetadata = {
  key: string;
  name: string;
  type: FormikStoreTypes;
  selectItems?: FormikStoreSelectItemsMetadata;
  editable?: boolean;
  visible?: boolean;
  placeholder?: FormikStoreType;
  required: boolean;
  errorText: string;
  alwaysValidate?: boolean;
  derivedViewModelKeysToClear?: string[]; // TODO currently implemented in SingleSelectDropdown!
  datesUTC?: boolean; // true treats dates as UTC values in object, but local time for display
  validator: FormikStoreValidator;
};

export type FormikStoreObject = {
  title: string;
  firestoreObject: {[key: string]: FormikStoreType};
  fieldMetadata: FormikStoreFieldMetadata[];
};

export const FormikStoreForm = (props: {
  objects: FormikStoreObject[];
  buttons: {
    text: string;
    validate: boolean;
    onPress: (formObjects: FormikStoreObject[]) => void;
  }[];
  validateForm?: boolean;
}) => {
  // const appSettings = useAppSettings();

  const validationObjects: {[key: string]: any} = {};
  const initialValues: {[key: string]: any} = {};
  props.objects.forEach(formikStoreObject => {
    formikStoreObject.fieldMetadata.forEach(metadata => {
      // set an initial value based on current object contents
      initialValues[metadata.key] =
        formikStoreObject.firestoreObject[metadata.key];

      switch (metadata.type) {
        case FormikStoreTypes.text:
        case FormikStoreTypes.textarea:
          validationObjects[metadata.key] = yup.string();
          // empty string if currently undefined
          if (!formikStoreObject.firestoreObject[metadata.key]) {
            initialValues[metadata.key] = '';
          }
          break;
        case FormikStoreTypes.number:
          validationObjects[metadata.key] = yup.number();
          // empty string if currently undefined
          if (!formikStoreObject.firestoreObject[metadata.key]) {
            initialValues[metadata.key] = '';
          }
          break;
        case FormikStoreTypes.date:
          validationObjects[metadata.key] = yup.date();
          break;
        case FormikStoreTypes.select:
          validationObjects[metadata.key] = yup.mixed();
          break;
        case FormikStoreTypes.arraySelect:
          validationObjects[metadata.key] = yup.mixed();
          break;
        case FormikStoreTypes.boolean:
          // false if currently undefined
          if (!formikStoreObject.firestoreObject[metadata.key]) {
            initialValues[metadata.key] = false;
          }
          validationObjects[metadata.key] = yup.boolean();
          break;
        default:
          console.log(`unknown type ${metadata.type} for validation`);
      }
      if (metadata.required && validationObjects[metadata.key]) {
        validationObjects[metadata.key] =
          validationObjects[metadata.key].required();
      }

      if (metadata.validator && validationObjects[metadata.key]) {
        validationObjects[metadata.key] = validationObjects[metadata.key].test(
          metadata.key + '-validator',
          metadata.errorText ?? '',
          (value: any) =>
            metadata.validator(value, metadata.required, props.objects),
        );
      }
    });
  });

  const validationSchema = yup.object(validationObjects);

  return (
    <View style={[styles.content, styles.center]}>
      <Headline>{props.objects[0].title}</Headline>
      <ScrollView contentContainerStyle={styles.scrollViewStyle}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={() => {
            // console.log('we are submitting');
            props.buttons[1].onPress(props.objects);
          }}>
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <View style={styles.content}>
              {props.objects.map(formikStoreObject => {
                // console.log('rendering ' + formikStoreObject.title);
                return formikStoreObject.fieldMetadata.map(metadata => {
                  // console.log('making view element for ' + metadata.name);
                  if (metadata.visible !== undefined && !metadata.visible) {
                    return;
                  }
                  const field = formikStoreObject.firestoreObject[metadata.key];
                  switch (metadata.type) {
                    case FormikStoreTypes.text: {
                      // console.log('working on type text');
                      const hasError =
                        touched[metadata.key] === true &&
                        !!errors[metadata.key];
                      return (
                        <View key={metadata.name} style={styles.rowStyle}>
                          <Text
                            style={styles.leftColumnStyle}
                            key={metadata.name}>
                            {metadata.name}:
                          </Text>
                          <FormikStoreTextInput
                            firestoreObject={formikStoreObject.firestoreObject}
                            metadata={metadata}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            hasError={hasError}
                          />
                        </View>
                      );
                    }
                    case FormikStoreTypes.textarea: {
                      // console.log('working on type textarea');
                      const hasError =
                        touched[metadata.key] === true &&
                        !!errors[metadata.key];
                      return (
                        <View key={metadata.name} style={styles.rowStyle}>
                          <Text
                            style={styles.leftColumnStyle}
                            key={metadata.name}>
                            {metadata.name}:
                          </Text>
                          <FormikStoreTextInput
                            firestoreObject={formikStoreObject.firestoreObject}
                            metadata={metadata}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            hasError={hasError}
                          />
                        </View>
                      );
                    }
                    case FormikStoreTypes.date: {
                      // console.log('working on type date');
                      const hasError =
                        touched[metadata.key] === true &&
                        !!errors[metadata.key];
                      return (
                        <View key={metadata.name} style={styles.rowStyle}>
                          <Text
                            style={styles.leftColumnStyle}
                            key={metadata.name}>
                            {metadata.name}:
                          </Text>
                          <SingleDatePicker
                            // FIXME how to handle timezones in the picker?
                            key={metadata.key}
                            firestoreObject={formikStoreObject.firestoreObject}
                            fieldKey={metadata.key}
                            metadata={metadata}
                            hasError={hasError}
                            setFieldValue={setFieldValue}
                            formikStoreObjects={props.objects}
                            datesUTC={metadata.datesUTC}
                          />
                        </View>
                      );
                    }
                    case FormikStoreTypes.number:
                      // console.log(
                      //   'working on type number (value: ' +
                      //     props.object.firestoreObject[fieldKey] +
                      //     ')',
                      // );
                      const hasError =
                        !!errors[metadata.key] &&
                        ((metadata.alwaysValidate ?? false) ||
                          touched[metadata.key] === true);
                      // console.log('touched is: ' + JSON.stringify(touched));
                      // console.log('errors is: ' + JSON.stringify(errors));
                      // console.log(
                      //   'alwaysValidate is: ' + metadata.alwaysValidate,
                      // );
                      // console.log('hasError? ' + hasError);
                      return (
                        <View style={styles.rowStyle} key={metadata.key}>
                          <Text style={styles.leftColumnStyle}>
                            {metadata.name}:
                          </Text>
                          <NumberInput
                            firestoreObject={formikStoreObject.firestoreObject}
                            fieldKey={metadata.key}
                            metadata={metadata}
                            formikStoreObjects={props.objects}
                            values={values}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            hasError={hasError}
                          />
                        </View>
                      );
                    case FormikStoreTypes.select:
                      const selectError =
                        touched[metadata.key] === true &&
                        !!errors[metadata.key];
                      return (
                        <View key={metadata.name} style={styles.rowStyle}>
                          <Text
                            style={styles.leftColumnStyle}
                            key={metadata.name}>
                            {metadata.name}:
                          </Text>
                          <SingleSelectDropdown
                            key={metadata.key}
                            firestoreObject={formikStoreObject.firestoreObject}
                            fieldKey={metadata.key}
                            metadata={metadata}
                            hasError={selectError}
                            setFieldValue={setFieldValue}
                          />
                          {/* <Text style={styles.rightColumnStyle}>
                          {field?.toString()}
                        </Text> */}
                        </View>
                      );
                    case FormikStoreTypes.arraySelect:
                      const arraySelectError =
                        touched[metadata.key] !== undefined &&
                        !!errors[metadata.key];
                      return (
                        <View key={metadata.name} style={styles.rowStyle}>
                          <Text
                            style={styles.leftColumnStyle}
                            key={metadata.name}>
                            {metadata.name}:
                          </Text>
                          <ArraySelect
                            key={metadata.key}
                            firestoreObject={formikStoreObject.firestoreObject}
                            fieldKey={metadata.key}
                            metadata={metadata}
                            hasError={arraySelectError}
                            setFieldValue={setFieldValue}
                          />
                          {/* <Text style={styles.rightColumnStyle}>
                            {field?.toString()}
                          </Text> */}
                        </View>
                      );
                    case FormikStoreTypes.boolean:
                      return (
                        <View style={styles.rowStyle} key={metadata.key}>
                          <Text style={styles.leftColumnStyle}>
                            {metadata.name}:
                          </Text>
                          <BooleanInput
                            firestoreObject={formikStoreObject.firestoreObject}
                            metadata={metadata}
                            setFieldValue={setFieldValue}
                          />
                        </View>
                      );
                    default:
                      console.log('working on type other');
                      return (
                        <View key={metadata.name} style={styles.rowStyle}>
                          <Text
                            style={styles.leftColumnStyle}
                            key={metadata.name}>
                            {metadata.name}:
                          </Text>
                          <Text style={styles.rightColumnStyle}>
                            {field?.toString()}
                          </Text>
                        </View>
                      );
                  }
                });
              })}

              <View style={styles.horizontalRight}>
                {props.buttons.map(button => {
                  if (button.validate) {
                    return (
                      <Button key={button.text} onPress={handleSubmit}>
                        {button.text}
                      </Button>
                    );
                  } else {
                    return (
                      <Button
                        key={button.text}
                        onPress={() => button.onPress(props.objects)}>
                        {button.text}
                      </Button>
                    );
                  }
                })}
              </View>
            </View>
          )}
        </Formik>
      </ScrollView>
    </View>
  );
};

function ArraySelect(props: {
  firestoreObject: {[key: string]: FormikStoreType};
  fieldKey: string;
  metadata: FormikStoreFieldMetadata;
  hasError: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}) {
  const appSettings = useAppSettings();
  const layoutInfo = useLayoutInfo();
  const [open, setOpen] = useState(false);
  let fsObjectField = props.firestoreObject[props.fieldKey] as Array<
    | string
    | number
    | boolean
    | Date
    | FirebaseFirestoreTypes.Timestamp
    | FirebaseFirestoreTypes.DocumentReference
  >;
  const documents = props.metadata.selectItems?.documents;

  // Make sure we have appropriate props so we may safely de-reference for use
  if (props.metadata.selectItems === undefined) {
    throw new Error('ArraySelect called with undefined selectItems');
  }
  if (documents !== undefined) {
    if (props.metadata.selectItems.labelExtractor === undefined) {
      throw new Error('ArraySelect with documents must have labelExtractor');
    }
  }

  // convert items as primitives or by label extraction / ref mapping if documents are specified
  let convertedItems: ItemType[] = [];
  if (documents !== undefined) {
    convertedItems = documents.map((doc, index) => {
      return {
        label: props.metadata.selectItems!!.labelExtractor!!(doc),
        value: (
          props.metadata.selectItems!!.items[
            index
          ] as FirebaseFirestoreTypes.DocumentReference
        ).path,
      };
    });
  } else {
    convertedItems = props.metadata.selectItems.items.map(item => {
      return {
        label: item + '',
        value: item + '',
      };
    });
  }

  const [dropdownItems, setDropdownItems] =
    useState<ItemType[]>(convertedItems);

  const themedStyles = StyleSheet.create({
    modalPickerContent: {
      margin: 160,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 160,
      maxHeight: layoutInfo.screen.height - 200,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  const removeValue = (value: FormikStoreType) => {
    props.firestoreObject[props.fieldKey] =
      fsObjectField.filter(existingValue => {
        return documents
          ? (existingValue as FirebaseFirestoreTypes.DocumentReference).path !==
              (value as FirebaseFirestoreTypes.DocumentReference).path
          : existingValue !== value;
      }) ?? [];
    props.setFieldValue(
      props.metadata.key,
      props.firestoreObject[props.fieldKey],
    );
  };

  return (
    <View style={styles.columnStyle}>
      {fsObjectField.map((value, index) => (
        <View key={'arrayselect' + index} style={styles.horizontalRight}>
          <Text>
            {index + 1 + '. '}
            {documents !== undefined &&
              props.metadata.selectItems!!.labelExtractor!!(
                documents?.find(
                  doc =>
                    doc.ref.path ===
                    (value as FirebaseFirestoreTypes.DocumentReference).path,
                ),
              )}
            {documents === undefined && value}
          </Text>
          {(props.metadata.editable ?? true) && (
            <IconButton
              icon="delete"
              size={18}
              color={appSettings.currentTheme.colors.primary}
              onPress={() => removeValue(value)}
            />
          )}
        </View>
      ))}
      <DropDownPicker
        open={open}
        value={null}
        items={dropdownItems}
        listMode="MODAL"
        disabled={!(props.metadata.editable ?? true)}
        containerStyle={styles.dropdownContainerStyle}
        modalTitle={props.metadata.name + ' Picker'}
        closeIconContainerStyle={styles.selectCloseIconContainerStyle}
        modalTitleStyle={styles.selectModalTitleStyle}
        onSelectItem={item => {
          let value: FormikStoreType = item.value!;
          // If documents were specified, value is a DocumentReference.path, get the DocumentReference
          if (documents !== undefined) {
            props.metadata.selectItems!.items.every(ref => {
              if (
                (ref as FirebaseFirestoreTypes.DocumentReference).path ===
                item.value
              ) {
                value = ref;
                return false;
              }
              return true;
            });
          }
          (
            props.firestoreObject[props.fieldKey] as Array<
              | string
              | number
              | boolean
              | Date
              | FirebaseFirestoreTypes.Timestamp
              | FirebaseFirestoreTypes.DocumentReference
            >
          ).push(value);

          props.setFieldValue(
            props.metadata.key,
            props.firestoreObject[props.fieldKey],
          ); // this triggers validation, so errors can clear
        }}
        placeholder={'Pick ' + props.metadata.name}
        modalProps={{transparent: true}}
        style={styles.rightColumnStyle}
        modalContentContainerStyle={themedStyles.modalPickerContent}
        setOpen={setOpen}
        setValue={() => {}}
        setItems={setDropdownItems}
        theme={appSettings.colorScheme === 'light' ? 'LIGHT' : 'DARK'}
      />
      <HelperText
        type="error"
        visible={props.hasError && !!props.metadata.errorText}>
        {props.metadata.errorText}
      </HelperText>
    </View>
  );
}

function SingleSelectDropdown(props: {
  firestoreObject: {[key: string]: FormikStoreType};
  fieldKey: string;
  metadata: FormikStoreFieldMetadata;
  hasError: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}) {
  const appSettings = useAppSettings();
  const layoutInfo = useLayoutInfo();
  const [open, setOpen] = useState(false);

  // Make sure we have appropriate props so we may safely de-reference for use
  if (props.metadata.selectItems === undefined) {
    throw new Error('SingleSelectDropdown called with undefined selectItems');
  }
  if (props.metadata.selectItems.documents !== undefined) {
    if (props.metadata.selectItems.labelExtractor === undefined) {
      throw new Error(
        'SingleSelectDropdown with documents must have labelExtractor',
      );
    }
  }

  // convert items as primitives or by label extraction / ref mapping if documents are specified
  let convertedItems: ItemType[] = [];
  if (props.metadata.selectItems?.documents !== undefined) {
    convertedItems = props.metadata.selectItems.documents.map((doc, index) => {
      return {
        label: props.metadata.selectItems!!.labelExtractor!!(doc),
        value: (
          props.metadata.selectItems!!.items[
            index
          ] as FirebaseFirestoreTypes.DocumentReference
        ).path,
      };
    });
  } else {
    convertedItems = props.metadata.selectItems.items.map(item => {
      return {
        label: item + '',
        value: item + '',
      };
    });
  }

  // FIXME TODO if there is only one choice, should we pre-select it?
  // FIXME TODO should we allow you to *un*-select an option?

  // If the firestore object we are selecting for already has a value, initialize the dropdown
  let dropdownInitialValue = null;
  // console.log(
  //   `fieldKey is ${props.fieldKey} and value is ${
  //     props.firestoreObject[props.fieldKey]
  //   }`,
  // );
  if (props.firestoreObject[props.fieldKey] !== undefined) {
    // ...if documents are specified, the value is a DocumentReference - map value to the reference path
    if (props.metadata.selectItems?.documents !== undefined) {
      dropdownInitialValue = (
        props.firestoreObject[
          props.fieldKey
        ] as FirebaseFirestoreTypes.DocumentReference
      ).path;
    } else {
      dropdownInitialValue = props.firestoreObject[props.fieldKey] as string;
    }
  }

  const [value, setValue] = useState<ValueType | null>(dropdownInitialValue);
  const [dropdownItems, setDropdownItems] =
    useState<ItemType[]>(convertedItems);

  // console.log('single select error? ' + props.hasError);

  // console.log(
  //   'our firebaseObject key was: ' +
  //     JSON.stringify(props.firestoreObject[props.fieldKey]),
  // );
  const themedStyles = StyleSheet.create({
    modalPickerContent: {
      margin: 160,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 160,
      maxHeight: layoutInfo.screen.height - 200,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  return (
    <View style={styles.columnStyle}>
      <DropDownPicker
        open={open}
        value={value}
        items={dropdownItems}
        listMode="MODAL"
        disabled={!(props.metadata.editable ?? true)}
        containerStyle={styles.dropdownContainerStyle}
        modalTitle={props.metadata.name + ' Picker'}
        closeIconContainerStyle={styles.selectCloseIconContainerStyle}
        modalTitleStyle={styles.selectModalTitleStyle}
        onSelectItem={item => {
          // console.log('selected item! ' + JSON.stringify(item));
          props.firestoreObject[props.fieldKey] = item.value;

          // If documents were specified, value is a DocumentReference.path, get the DocumentReference
          if (props.metadata.selectItems?.documents !== undefined) {
            props.metadata.selectItems.items.forEach((ref, index) => {
              if (
                (ref as FirebaseFirestoreTypes.DocumentReference).path ===
                item.value
              ) {
                props.firestoreObject[props.fieldKey] =
                  props.metadata.selectItems!!.items[index];
              }
            });
          }

          props.metadata.derivedViewModelKeysToClear?.map(viewModelKey => {
            // console.log('clearing view model key: ' + viewModelKey);
            props.setFieldValue(viewModelKey, undefined, false);
          });

          props.setFieldValue(props.metadata.key, item.value); // this triggers validation, so errors can clear
          // console.log(
          //   'post-pick firestoreObject: ' +
          //     JSON.stringify(props.firestoreObject, null, 2),
          // );
        }}
        placeholder={'Pick ' + props.metadata.name}
        modalProps={{transparent: true}}
        style={styles.rightColumnStyle}
        modalContentContainerStyle={themedStyles.modalPickerContent}
        setOpen={setOpen}
        setValue={setValue}
        setItems={setDropdownItems}
        theme={appSettings.colorScheme === 'light' ? 'LIGHT' : 'DARK'}
      />
      <HelperText
        type="error"
        visible={props.hasError && !!props.metadata.errorText}>
        {props.metadata.errorText}
      </HelperText>
    </View>
  );
}

function SingleDatePicker(props: {
  firestoreObject: {[key: string]: FormikStoreType};
  fieldKey: string;
  metadata: FormikStoreFieldMetadata;
  formikStoreObjects: FormikStoreObject[];
  hasError: boolean;
  validating?: boolean;
  datesUTC?: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}) {
  // console.log(
  //   `date fieldKey is ${props.fieldKey} and value is ${
  //     props.firestoreObject[props.fieldKey]
  //   }`,
  // );

  var localTZOffset = new Date().getTimezoneOffset() * 60000;

  // const displayDate = addTZOffset(
  //   props.firestoreObject[props.fieldKey] as Date,
  // );

  function addTZOffset(utcDate: Date | undefined): Date | undefined {
    if (utcDate === undefined) {
      return undefined;
    }
    utcDate.setUTCHours(0);
    utcDate.setUTCMinutes(0);
    utcDate.setUTCSeconds(0);
    const localDate = new Date(utcDate.getTime() + localTZOffset);
    // console.log(
    //   `ReservationEditor::addTZOffset got ${date.toUTCString()} returning ${UTCDate.toUTCString()}`,
    // );
    return localDate;
  }

  const [utcDate, setDate] = useState<Date | undefined>(
    props.firestoreObject[props.fieldKey]
      ? (props.firestoreObject[props.fieldKey] as Date)
      : undefined,
  );

  const currentEndDate = props.firestoreObject[props.fieldKey] as Date;
  useEffect(() => {
    setDate(currentEndDate);
  }, [currentEndDate]);

  const [open, setOpen] = useState(false);

  const onDismissSingle = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onConfirmSingle = useCallback(
    params => {
      setOpen(false);
      // We want our dates to be at zero-hour but they come in 23:59:59
      params.date.setHours(0);
      params.date.setMinutes(0);
      params.date.setSeconds(0);

      // If object is using UTC dates, we need to remove TZ offset from display date
      // to set the UTC date correctly back into the object
      if (props.datesUTC) {
        const UTCDate = new Date(params.date.getTime() - localTZOffset);
        // UTCDate.setUTCHours(0);
        // UTCDate.setUTCMinutes(0);
        // UTCDate.setUTCSeconds(0);
        // console.log(
        //   `ReservationEditor::removeTZOffset got ${date.toUTCString()} returning ${UTCDate.toUTCString()}`,
        // );
        params.date = UTCDate;
      }

      setDate(params.date);
      // console.log(
      //   `date picked ${params.date} (or UTC ${params.date.toUTCString()})`,
      // );
      props.firestoreObject[props.fieldKey] = params.date;
      props.setFieldValue(props.metadata.key, params.date); // this triggers validation, so errors can clear
    },
    [setOpen, setDate, props, localTZOffset],
  );

  return (
    <>
      <Text>{addTZOffset(utcDate)?.toDateString()}</Text>
      {(props.metadata.editable ?? true) && (
        <>
          <Button
            onPress={() => setOpen(true)}
            uppercase={false}
            mode="outlined">
            Pick {props.metadata.name}
          </Button>
          <DatePickerModal
            locale="en"
            mode="single"
            visible={open}
            onDismiss={onDismissSingle}
            date={addTZOffset(utcDate)}
            onConfirm={onConfirmSingle}
            // validRange={{
            //   startDate: new Date(2021, 1, 2),  // optional
            //   endDate: new Date(), // optional
            //   disabledDates: [new Date()] // optional
            // }}
            // onChange={} // same props as onConfirm but triggered without confirmed by user
            // saveLabel="Save" // optional
            // uppercase={false} // optional, default is true
            // label="Select date" // optional
            // animationType="slide" // optional, default is 'slide' on ios/android and 'none' on web
          />
        </>
      )}
      <HelperText
        type="error"
        visible={props.hasError && !!props.metadata.errorText}>
        {props.metadata.errorText}
      </HelperText>
    </>
  );
}

const BooleanInput = (props: {
  metadata: FormikStoreFieldMetadata;
  firestoreObject: {[key: string]: FormikStoreType};
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}): JSX.Element => {
  const [checked, setChecked] = useState(
    props.firestoreObject[props.metadata.key] ? true : false,
  );
  return (
    <View style={styles.columnStyle}>
      <Checkbox
        onPress={() => {
          props.firestoreObject[props.metadata.key] = !checked;
          setChecked(!checked);
          props.setFieldValue(props.metadata.key, !checked); // this triggers validation, so errors can clear
        }}
        status={
          props.firestoreObject[props.metadata.key] ? 'checked' : 'unchecked'
        }
      />
    </View>
  );
};

const FormikStoreTextInput = (props: {
  firestoreObject: {[key: string]: FormikStoreType};
  metadata: FormikStoreFieldMetadata;
  validating?: boolean;
  hasError: boolean;
  handleChange: (key: string) => void;
  handleBlur: (e: any) => void;
}): JSX.Element => {
  return (
    <View style={styles.columnStyle}>
      {/* @ts-ignore TODO figure out why setting onBlur requires this !?! */}
      <TextInput
        mode="outlined"
        dense
        editable={props.metadata.editable}
        error={props.hasError}
        // @ts-ignore
        onChange={props.handleChange(props.metadata.key)}
        onChangeText={text => {
          // console.log('setChangeText with ' + text);
          props.firestoreObject[props.metadata.key] = text;
        }}
        onBlur={props.handleBlur(props.metadata.key)}
        style={styles.rightColumnStyle}
        value={props.firestoreObject[props.metadata.key]}
        placeholder="Enter text"
      />
      <HelperText
        type="error"
        visible={props.hasError && !!props.metadata.errorText}>
        {props.metadata.errorText}
      </HelperText>
    </View>
  );
};

const NumberInput = (props: {
  firestoreObject: {[key: string]: FormikStoreType};
  fieldKey: string;
  metadata: FormikStoreFieldMetadata;
  formikStoreObjects: FormikStoreObject[];
  validating?: boolean;
  values: {[key: string]: any};
  hasError: boolean;
  handleChange: (key: string) => void;
  handleBlur: (e: any) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}): JSX.Element => {
  // console.log('current values: ' + JSON.stringify(props.values, null, 2));
  return (
    <View style={styles.columnStyle}>
      {/* @ts-ignore TODO figure out why setting onBlur requires this !?! */}
      <TextInput
        mode="outlined"
        dense
        editable={props.metadata.editable}
        error={props.hasError}
        // @ts-ignore
        onChange={props.handleChange(props.metadata.key)}
        onChangeText={text => {
          // console.log('setChangeText with ' + text);
          props.firestoreObject[props.metadata.key] = text;

          // Turn it in to an actual number if it is possible - `+` operator leaves partial-text alone vs parseInt
          if (text) {
            const parsedInput = +text;
            if (!isNaN(parseFloat(text)) && !Number.isNaN(parsedInput)) {
              // if (!isNaN(parsedInput)) {
              props.firestoreObject[props.metadata.key] = text;
            }
          }
          // values[metadata.key] = text;
          // handleChange(metadata.key);
          // setValue(text);
          // setValue(Number.parseInt(text, 10));
          // console.log(
          //   'current firestoreObject: ' +
          //     JSON.stringify(props.firestoreObject, null, 2),
          // );
        }}
        onBlur={props.handleBlur(props.metadata.key)}
        style={styles.rightColumnStyle}
        value={props.firestoreObject[props.metadata.key]?.toString()}
        placeholder="Enter number"
      />
      <HelperText
        type="error"
        visible={props.hasError && !!props.metadata.errorText}>
        {props.metadata.errorText}
      </HelperText>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    padding: 20,
    flex: 1,
  },
  dropdownContainerStyle: {
    borderRadius: 3,
    borderWidth: 1,
  },
  scrollViewStyle: {
    // borderWidth: 1,
    // borderColor: 'green',
    flex: 1,
    flexDirection: 'column',
  },
  columnStyle: {
    flex: 1,
    flexDirection: 'column',
  },
  rowStyle: {
    // borderWidth: 1,
    // borderColor: 'blue',
    // flex: 1,
    padding: 5,
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'space-between',
  },
  leftColumnStyle: {
    flexDirection: 'row',
    alignContent: 'flex-end',
    textAlign: 'right',
    minWidth: '30%',
    width: '30%',
    padding: 10,
  },
  rightColumnStyle: {
    flexDirection: 'row',
    alignContent: 'flex-start',
    textAlign: 'left',
    width: '70%',
    // padding: 10,
  },
  center: {
    width: '100%',
    alignSelf: 'center',
    textAlign: 'center',
    justifyContent: 'center',
  },
  horizontalRight: {
    flexDirection: 'row',
    alignContent: 'flex-end',
    justifyContent: 'flex-end',
  },
  selectCloseIconContainerStyle: {
    justifyContent: 'flex-end',
    alignContent: 'flex-end',
    alignItems: 'flex-end',
    alignSelf: 'flex-end',
  },
  selectModalTitleStyle: {
    padding: 20,
    justifyContent: 'center',
    alignContent: 'center',
    alignSelf: 'center',
    flexDirection: 'row',
  },
});
